import { useState } from 'react'
import { createPortal } from 'react-dom'

import { fehlertext } from '../../api/client'
import type { Message } from '../../api/types'
import { XIcon } from '../../components/Icons'
import { useHighlights, useTrip, useVeroeffentlichen } from '../../hooks/queries'
import { useEscape } from '../../hooks/useEscape'
import { buchTag, uhrText } from '../../lib/ortszeit'
import { useTexte } from '../../texte'

/**
 * "Bis hierher veröffentlichen" (#85, 1.21.0): the sheet behind the long
 * press on a draft.
 *
 * It says what is about to go out -- how many posts, written between when
 * and when -- and offers the map points of that span along. The count of
 * points is derived from the highlights the map already holds; the server
 * decides again on its own when publishing, this number is the preview.
 *
 * A portal at `document.body`, like every sheet here: the frosted material
 * makes any ancestor a containing block for `position: fixed` (CLAUDE.md).
 */
export function Veroeffentlichen({
  tripId,
  bis,
  entwuerfe,
  onFertig,
  onSchliessen,
}: {
  tripId: number
  /** The draft the finger was on -- everything up to it goes out. */
  bis: Message
  /** The draft as the page shows it: newest position first. */
  entwuerfe: Message[]
  onFertig: (firstId: number | null) => void
  onSchliessen: () => void
}) {
  const texte = useTexte()
  const t = texte.chat.entwurf
  const trip = useTrip(tripId)
  const versatz = trip.data?.tz_offset_min ?? null
  const highlights = useHighlights(tripId, true)
  const veroeffentlichen = useVeroeffentlichen(tripId)
  const [mitPunkten, setMitPunkten] = useState(true)
  const [problem, setProblem] = useState<string | null>(null)

  useEscape(onSchliessen)

  // The page lists newest position first; everything from the chosen one to
  // the end of the array is older and goes out with it.
  const start = entwuerfe.findIndex((m) => m.id === bis.id)
  const stapel = start < 0 ? [bis] : entwuerfe.slice(start)
  const zeiten = stapel.map((m) => Date.parse(m.created_at))
  const anfang = Math.min(...zeiten)
  const ende = Math.max(...zeiten)
  const anfangIso = new Date(anfang).toISOString()
  const endeIso = new Date(ende).toISOString()

  const punkte = (highlights.data?.highlights ?? []).filter((h) => {
    if (h.published_at !== null || h.origin !== 'manual' || h.happened_at === null) return false
    const zeitpunkt = Date.parse(h.happened_at)
    return zeitpunkt >= anfang && zeitpunkt <= ende
  }).length

  async function los() {
    setProblem(null)
    try {
      const ergebnis = await veroeffentlichen.mutateAsync({
        untilId: bis.id,
        withHighlights: punkte > 0 && mitPunkten,
      })
      onFertig(ergebnis.first_id)
    } catch (fehler) {
      setProblem(fehlertext(fehler))
    }
  }

  return createPortal(
    <div
      className="bogen bogen--obenauf"
      role="dialog"
      aria-modal="true"
      aria-label={t.blattTitel}
      data-veroeffentlichen=""
      onPointerDown={(event) => event.stopPropagation()}
    >
      <div className="bogen__blatt">
        <div className="bogen__kopf">
          <h3 className="bogen__titel">{t.blattTitel}</h3>
          <button
            type="button"
            className="icon-button"
            onClick={onSchliessen}
            aria-label={texte.allgemein.schliessen}
            title={texte.allgemein.schliessen}
          >
            <XIcon size={18} />
          </button>
        </div>
        <p className="bogen__hinweis" data-veroeffentlichen-anzahl={stapel.length}>
          {t.beitraege(stapel.length)} · {t.zeitraum(buchTag(anfangIso, versatz), uhrText(anfangIso, versatz), uhrText(endeIso, versatz))}
        </p>
        {punkte > 0 ? (
          <label className="row row--wahl">
            <input
              type="checkbox"
              className="wahl__haken"
              checked={mitPunkten}
              onChange={(event) => setMitPunkten(event.target.checked)}
              data-veroeffentlichen-punkte=""
            />
            <span>{t.punkteMit(punkte)}</span>
          </label>
        ) : (
          <p className="bogen__hinweis leise">{t.keinePunkte}</p>
        )}
        <p className="bogen__hinweis leise">{t.hinweis}</p>
        {problem !== null && (
          <p className="bogen__problem" role="alert">
            {problem}
          </p>
        )}
        <div className="bogen__fuss">
          <button type="button" className="button button--quiet" onClick={onSchliessen}>
            {texte.allgemein.abbrechen}
          </button>
          <button
            type="button"
            className="button"
            disabled={veroeffentlichen.isPending}
            onClick={() => void los()}
            data-veroeffentlichen-knopf=""
          >
            {veroeffentlichen.isPending ? t.laeuft : t.knopf(stapel.length)}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
